import { useEffect, useState } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CandidateOffersBanner() {
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/me/offers`);
      setOffers(data || []);
    } catch (err) { logger.error('load offers', err); }
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    const reason = decision === 'declined' ? window.prompt('Optional reason for declining:') || '' : '';
    setBusy(true);
    try {
      await axios.post(`${API}/offers/${id}/decision`, { decision, reason });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (!offers.length) return null;

  return (
    <div className="mb-6 space-y-3" data-testid="candidate-offers-banner">
      {offers.map((o) => (
        <div key={o.id} className="rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 shadow-lg" data-testid={`pending-offer-${o.id}`}>
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex-1 min-w-[240px]">
              <div className="text-xs uppercase tracking-wider text-emerald-100 mb-1">You have an offer</div>
              <div className="text-2xl font-bold" data-testid={`offer-salary-${o.id}`}>
                {o.salary_currency} {Number(o.salary_amount).toLocaleString()}
                {o.bonus_pct ? <span className="text-base font-medium"> + {o.bonus_pct}% bonus</span> : null}
              </div>
              <div className="text-sm text-emerald-50 mt-1">
                Start {o.start_date} · Expires {o.expires_at?.slice(0, 10)}
                {o.location ? ` · ${o.location}` : ''}
              </div>
              {o.notes_to_candidate && (
                <div className="text-sm text-emerald-50 mt-2 italic">"{o.notes_to_candidate}"</div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => decide(o.id, 'accepted')}
                disabled={busy}
                data-testid={`accept-offer-${o.id}`}
                className="px-5 py-2 rounded-lg bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition disabled:opacity-50"
              >
                Accept
              </button>
              <button
                onClick={() => decide(o.id, 'declined')}
                disabled={busy}
                data-testid={`decline-offer-${o.id}`}
                className="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm border border-white/30"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


export function AnalyticsDashboard() {
  const [funnel, setFunnel] = useState(null);
  const [tth, setTth] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [sources, setSources] = useState(null);
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/analytics/funnel`).then((r) => setFunnel(r.data)).catch(() => {}),
      axios.get(`${API}/analytics/time-to-hire`).then((r) => setTth(r.data)).catch(() => {}),
      axios.get(`${API}/analytics/jobs`).then((r) => setJobs(r.data)).catch(() => {}),
      axios.get(`${API}/analytics/sources`).then((r) => setSources(r.data)).catch(() => {}),
      axios.get(`${API}/analytics/recommendations`).then((r) => setRecs(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-500">Loading analytics…</div>;

  const maxFunnel = funnel ? Math.max(...funnel.funnel.map((f) => f.count), 1) : 1;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Published jobs" value={jobs?.published_jobs ?? '–'} testid="kpi-published-jobs" />
        <KPI label="Apps (last 30d)" value={jobs?.applications_last_30_days ?? '–'} testid="kpi-apps-30d" />
        <KPI label="Hired" value={tth?.hired_count ?? 0} testid="kpi-hired" />
        <KPI label="Avg time-to-hire" value={tth?.average_days != null ? `${tth.average_days} d` : '–'} testid="kpi-tth" />
      </div>

      {funnel && (
        <div className="bg-white rounded-lg shadow p-5" data-testid="funnel-chart">
          <h3 className="font-semibold mb-4">Hiring Funnel — {funnel.total_applications} apps</h3>
          <div className="space-y-2">
            {funnel.funnel.map((f) => (
              <div key={f.stage} data-testid={`funnel-stage-${f.stage}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="capitalize font-medium">{f.stage}</span>
                  <span>{f.count}{f.conversion_pct != null ? ` (${f.conversion_pct}%)` : ''}</span>
                </div>
                <div className="bg-gray-100 rounded h-3 overflow-hidden">
                  <div
                    className="bg-indigo-600 h-full rounded"
                    style={{ width: `${(f.count / maxFunnel) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sources && sources.sources.length > 0 && (
        <div className="bg-white rounded-lg shadow p-5" data-testid="sources-chart">
          <h3 className="font-semibold mb-4">Application Sources</h3>
          <div className="space-y-2">
            {sources.sources.map((s) => (
              <div key={s.source} className="flex justify-between text-sm">
                <span className="capitalize">{s.source.replace(/_/g, ' ')}</span>
                <span className="font-mono">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {recs && recs.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow p-5" data-testid="recs-chart">
          <h3 className="font-semibold mb-4">Scorecard Recommendations (last 90 days)</h3>
          <div className="space-y-2">
            {recs.recommendations.map((r) => (
              <div key={r.recommendation} className="flex justify-between text-sm">
                <span className="capitalize">{r.recommendation.replace(/_/g, ' ')}</span>
                <span><span className="text-gray-500">⭐ {r.average_rating}</span> · <span className="font-mono">{r.count}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="bg-white rounded-lg shadow p-4" data-testid={testid}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
